"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

export type Theme = "dark" | "light";
export type Rg = "cn" | "intl";

interface ThemeContextValue {
  theme: Theme;
  rg: Rg;
  toggleTheme: () => void;
  setRg: (rg: Rg) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const THEME_KEY = "invest.theme";
const RG_KEY = "invest.rg";

// 默认值必须与 layout.tsx 的 <html data-theme/data-rg> 一致，避免 hydration 闪烁（doc 08 §13）。
// 2026-07-29：默认主题定为亮色「机构金融亮色BI」（暗色仍可通过开关切换，选择持久化在 localStorage）。
const DEFAULT_THEME: Theme = "light";
const DEFAULT_RG: Rg = "cn";

/**
 * 管理暗/亮主题与红涨绿跌语义，写到 documentElement 的 data-theme / data-rg，
 * 并持久化到 localStorage。挂载后再从 localStorage 同步，防止 SSR/CSR 不一致。
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(DEFAULT_THEME);
  const [rg, setRgState] = useState<Rg>(DEFAULT_RG);

  // 挂载后从 localStorage 恢复。必须在 effect 里 setState：服务端无 localStorage，
  // 首屏须用与 <html> 默认值一致的 DEFAULT_*，挂载后再校正，否则 hydration 不一致（doc 08 §13）。
  /* eslint-disable react-hooks/set-state-in-effect -- 挂载后一次性同步 localStorage，非渲染派生 */
  useEffect(() => {
    const savedTheme = localStorage.getItem(THEME_KEY) as Theme | null;
    const savedRg = localStorage.getItem(RG_KEY) as Rg | null;
    if (savedTheme === "dark" || savedTheme === "light") setTheme(savedTheme);
    if (savedRg === "cn" || savedRg === "intl") setRgState(savedRg);
  }, []);
  /* eslint-enable react-hooks/set-state-in-effect */

  // 同步到 <html> 属性 + 持久化
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.setAttribute("data-rg", rg);
    localStorage.setItem(RG_KEY, rg);
  }, [rg]);

  const toggleTheme = useCallback(() => {
    setTheme((t) => (t === "dark" ? "light" : "dark"));
  }, []);

  const setRg = useCallback((next: Rg) => setRgState(next), []);

  return (
    <ThemeContext.Provider value={{ theme, rg, toggleTheme, setRg }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme 必须在 ThemeProvider 内使用");
  return ctx;
}
